import React from "react";
import { themeIcons } from "seti-icons";

/** Default icon size (px) used across the tree */
export const FILE_ICON_SIZE = 16;

/**
 * Seti UI colour theme — these map to the named colour keys
 * returned by seti-icons (e.g. "blue", "green", "orange").
 * Adjust these hex values to match your app's theme.
 */
const setiTheme = {
    blue:         "#519aba",
    grey:         "#4d5a5e",
    "grey-light": "#6d8086",
    green:        "#8dc149",
    orange:       "#e37933",
    pink:         "#f55385",
    purple:       "#a074c4",
    red:          "#cc3e44",
    white:        "#d4d7d6",
    yellow:       "#cbcb41",
    ignore:       "#41535b",
};

/** Themed icon resolver — returns { svg, color } with hex colour */
const getThemedIcon = themeIcons(setiTheme);

/**
 * Renders a Seti UI SVG icon for the given filename.
 *
 * `seti-icons` handles all resolution internally:
 *   1. Exact filename match (e.g. Dockerfile, .gitignore)
 *   2. Extension match (e.g. .ts, .py, .json)
 *   3. Partial match (e.g. filenames containing "test")
 *   4. Default file icon
 */
export function getFileIcon(
    filename: string,
    size: number = FILE_ICON_SIZE
): React.ReactElement {
    const { svg, color } = getThemedIcon(filename);

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: size,
                height: size,
                flexShrink: 0,
                color,
            }}
            dangerouslySetInnerHTML={{
                __html: svg.replace(
                    "<svg",
                    `<svg width="${size}" height="${size}" style="fill: currentColor;"`
                ),
            }}
        />
    );
}

/**
 * Returns a folder icon using Seti's folder SVG.
 * Pass a dummy folder-like name to get the folder icon from seti-icons.
 */
export function getFolderIcon(
    _isOpen: boolean,
    size: number = FILE_ICON_SIZE
): React.ReactElement {
    // seti-icons doesn't differentiate open/closed folders,
    // so we use the same icon for both. The tree chevron indicates state.
    const { svg, color } = getThemedIcon("folder");

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: size,
                height: size,
                flexShrink: 0,
                color,
            }}
            dangerouslySetInnerHTML={{
                __html: svg.replace(
                    "<svg",
                    `<svg width="${size}" height="${size}" style="fill: currentColor;"`
                ),
            }}
        />
    );
}
